import React from 'react';
import { Dialog, DialogContent, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useTranslation } from 'react-i18next';
import { AnimalPhotoWithUrl } from '../../models/animal-photos';

export interface AnimalPhotoViewerProps {
  photos: AnimalPhotoWithUrl[];
  /** The photo on screen, or null when the viewer is closed. */
  index: number | null;
  onIndexChange: (index: number | null) => void;
  formatCaptured: (photo: AnimalPhotoWithUrl) => string;
}

/**
 * One photo at full size, stepping through the photos of the current page with the arrows or the
 * keyboard. It shows the same link as the thumbnail, so the browser serves it from cache.
 */
const AnimalPhotoViewer: React.FC<AnimalPhotoViewerProps> = ({ photos, index, onIndexChange, formatCaptured }) => {
  const { t } = useTranslation();
  const photo = index !== null ? photos[index] : undefined;
  const hasPrevious = index !== null && index > 0;
  const hasNext = index !== null && index < photos.length - 1;

  const step = React.useCallback(
    (delta: number) => {
      if (index === null) return;
      const next = index + delta;
      if (next >= 0 && next < photos.length) onIndexChange(next);
    },
    [index, photos.length, onIndexChange]
  );

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowLeft') step(-1);
    if (event.key === 'ArrowRight') step(1);
  };

  return (
    <Dialog open={photo !== undefined} onClose={() => onIndexChange(null)} maxWidth="lg" onKeyDown={handleKeyDown}>
      {photo && (
        <DialogContent className="!p-0 relative bg-black" id="animal-photo-viewer">
          <IconButton
            aria-label={t('f-com-close')}
            onClick={() => onIndexChange(null)}
            className="!absolute !top-2 !right-2 !bg-black/50 !text-white hover:!bg-black/70"
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>

          {photo.url ? (
            <img
              src={photo.url}
              alt={t('f-animal-photos-alt', { captured: formatCaptured(photo) })}
              className="block max-w-full max-h-[80vh] mx-auto"
            />
          ) : (
            <div className="w-[60vw] h-[50vh] flex items-center justify-center text-gray-300">
              {t('f-animal-photos-unavailable', { defaultValue: 'Photo unavailable' })}
            </div>
          )}

          {hasPrevious && (
            <IconButton
              aria-label={t('f-com-previous')}
              onClick={() => step(-1)}
              className="!absolute !left-2 !top-1/2 !-translate-y-1/2 !bg-black/50 !text-white hover:!bg-black/70"
            >
              <ChevronLeftIcon />
            </IconButton>
          )}
          {hasNext && (
            <IconButton
              aria-label={t('f-com-next')}
              onClick={() => step(1)}
              className="!absolute !right-2 !top-1/2 !-translate-y-1/2 !bg-black/50 !text-white hover:!bg-black/70"
            >
              <ChevronRightIcon />
            </IconButton>
          )}

          <div className="flex items-center justify-between gap-4 px-4 py-2 bg-black/80 text-white text-sm">
            <span>
              {t('f-animal-photos-captured', { defaultValue: 'Taken' })}: {formatCaptured(photo)}
            </span>
            <span className="text-gray-300">
              {(index ?? 0) + 1} / {photos.length}
            </span>
          </div>
        </DialogContent>
      )}
    </Dialog>
  );
};

export default AnimalPhotoViewer;
